import { useEffect, useId, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { searchPlaces } from "@/api/nominatim";
import type { NominatimPlace } from "@/api/nominatim";

interface LocationInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Suggestions from local history (always shown as chips below the input) */
  historySuggestions?: string[];
  /** When true, queries Nominatim as the user types */
  liveSearch?: boolean;
}

export function LocationInput({
  label,
  value,
  onChange,
  placeholder,
  historySuggestions = [],
  liveSearch = false,
}: LocationInputProps) {
  const [results, setResults] = useState<NominatimPlace[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputId = useId();

  // Nominatim search — debounced 400 ms
  useEffect(() => {
    if (!liveSearch) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const places = await searchPlaces(value);
        setResults(places);
        setOpen(places.length > 0);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, liveSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function select(name: string) {
    onChange(name);
    setOpen(false);
    setResults([]);
  }

  const filteredHistory = historySuggestions.filter(
    (s) => s !== value && (value === "" || s.toLowerCase().includes(value.toLowerCase()))
  );

  return (
    <div ref={containerRef}>
      <label
        htmlFor={inputId}
        className="text-xs text-text-muted uppercase tracking-widest block mb-1.5"
      >
        {label}
      </label>

      <div className="relative">
        <input
          id={inputId}
          type="text"
          className="input pr-8"
          placeholder={placeholder}
          value={value}
          onChange={(e) => { onChange(e.target.value); }}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          required
          autoComplete="off"
          data-testid={`location-input-${label.toLowerCase()}`}
        />
        {searching && (
          <Loader2
            size={14}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted animate-spin"
          />
        )}

        {/* Nominatim dropdown */}
        {open && results.length > 0 && (
          <div className="absolute z-10 top-full mt-1 left-0 right-0 bg-bg-base border border-white/10 rounded-xl overflow-hidden shadow-xl">
            {results.map((r, i) => (
              <button
                key={i}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); select(r.name); }}
                className="w-full text-left px-4 py-2.5 hover:bg-white/5 transition-colors flex items-baseline gap-2"
              >
                <span className="text-text-primary text-sm">{r.name}</span>
                {r.shortLocation && (
                  <span className="text-text-muted text-xs truncate">{r.shortLocation}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* History chips */}
      {filteredHistory.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {filteredHistory.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => select(s)}
              className="px-2.5 py-1 rounded-full text-xs border border-white/10 text-text-muted hover:border-accent hover:text-accent transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
